import { Injectable, inject } from '@angular/core';
import { UserMasterProxyService } from '../proxies/user-master.proxy.service';
import { jwtDecode } from 'jwt-decode';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { LoginBody } from '../models/user-master/commands/login-body';
import { Router } from '@angular/router';
import { JwtPayload } from '../models/user-master/jwt-payload';
import { EnumService } from './enum.service';

@Injectable({
  providedIn: 'root',
})
export class UserAuthenticationService {
  private userMasterProxyService = inject(UserMasterProxyService);
  private enumService = inject(EnumService);
  private router = inject(Router);

  private readonly tokenKey = 'SierraToken';

  public login(loginBody: LoginBody): Observable<boolean> {
    return this.userMasterProxyService.login(loginBody).pipe(
      tap((response) => {
        if (!response || !response.data) {
          throw new Error('Login failed, no token received');
        }
        localStorage.setItem(this.tokenKey, response.data);
      }),
      switchMap(() => this.enumService.loadEnums()),
      map(() => true),
      catchError((error) => {
        console.error('Error logging in', error);
        return of(false);
      }),
    );
  }

  public logout() {
    localStorage.removeItem(this.tokenKey);
    this.router.navigate(['/login']);
  }

  public get userLite() {
    const token = this.getStoredJwtToken();
    if (!token) {
      return null;
    }

    // Decode the token and map to user details
    const { FirstName, LastName, Email, Username }: JwtPayload = jwtDecode<JwtPayload>(token);
    return { firstName: FirstName, lastName: LastName, email: Email, username: Username };
  }

  public get isUserAuthenticated(): boolean {
    const token = this.getStoredJwtToken();
    if (!token) {
      return false;
    }
    const isTokenExpired = this.isTokenExpired(token);
    if (isTokenExpired) {
      this.logout();
      return false;
    }
    return true;
  }

  public validateTokenAndAuthenticate(token: string): boolean {
    if (!token || this.isTokenExpired(token)) {
      console.warn('Token is invalid or expired');
      return false;
    }

    this.saveToken(token);
    this.enumService.loadEnums().subscribe();

    return true;
  }

  saveToken(token: string) {
    localStorage.setItem(this.tokenKey, token);
  }
  getStoredJwtToken() {
    return localStorage.getItem(this.tokenKey);
  }

  isTokenExpired(token: string) {
    try {
      const { exp }: { exp: number } = jwtDecode(token);
      return exp * 1000 < Date.now();
    } catch (e) {
      console.error('Error decoding token', e);
      return true;
    }
  }
}
